// Reported financials for one company — read straight from company_financials,
// which the sync (netlify/functions/lib/edgarFinancials.js) filled from SEC
// companyfacts. Every row carries the filing it came from. Nothing here
// computes a number the company did not report, with ONE labelled exception:
// free cash flow, derived from two reported rows of the same period and
// returned under `derived`, never mixed into the reported concepts.

import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";

// Display order and Hebrew labels. `kind` decides how the trend reads:
// a flow is a year's total, a balance is a point in time.
export const CONCEPTS = [
  { id: "revenue",          label: "הכנסות",             kind: "flow" },
  { id: "gross_profit",     label: "רווח גולמי",          kind: "flow" },
  { id: "operating_income", label: "רווח תפעולי",         kind: "flow" },
  { id: "net_income",       label: "רווח נקי",            kind: "flow" },
  { id: "cfo",              label: "תזרים מפעילות שוטפת", kind: "flow" },
  { id: "capex",            label: "השקעות הוניות (CapEx)", kind: "flow" },
  { id: "cash",             label: "מזומנים ושווי מזומנים", kind: "balance" },
  { id: "long_term_debt",   label: "חוב לזמן ארוך",       kind: "balance" },
  { id: "debt_current",     label: "חוב לזמן קצר",        kind: "balance" },
  { id: "rpo",              label: "צבר הזמנות (RPO)",    kind: "balance" },
];

// Why a concept can be absent — stated as how companies report, not as a
// defect. Shown in place of the row. A concept with no entry here gets the
// generic line.
export const NOT_REPORTED_WHY = {
  gross_profit:     "החברה אינה מדווחת שורת רווח גולמי · נפוץ בבנקים, בביטוח ובחברות שמציגות עלויות לפי סוג ולא כעלות המכר",
  operating_income: "אין שורת רווח תפעולי בדוח · בנקים וחלק מחברות הפארמה מציגות רווח לפני מס בלבד",
  capex:            "אין שורת השקעות הוניות · בנקים מדווחים רכישות רכוש קבוע בתוך שורות אחרות",
  rpo:              "החברה אינה מדווחת צבר הזמנות כסעיף · נדרש רק כשיש התחייבויות ביצוע ארוכות, ורבות מגלות זאת בטקסט בלבד",
  long_term_debt:   "לא דווח חוב לזמן ארוך · לחברה אין הלוואות או אג\"ח בסעיף זה, או שהיא מדווחת אותו בסעיף אחר",
  debt_current:     "לא דווח חוב לזמן קצר · אין הלוואות שוטפות בסעיף זה, או שהוא מדווח בסעיף אחר",
  cash:             "לא נמצא סעיף מזומנים · החברה מדווחת את המזומן בסעיף אחר",
  revenue:          "לא נמצא סעיף הכנסות אחיד · החברה מדווחת הכנסות בפירוט לפי סוג בלבד",
  net_income:       "לא נמצא סעיף רווח נקי",
  cfo:              "לא נמצא סעיף תזרים מפעילות שוטפת",
};
export const NOT_REPORTED_GENERIC = "החברה אינה מדווחת סעיף זה בדוח השנתי";

const EMPTY = { company: null, byConcept: {}, derived: {}, latestFiling: null };

async function loadFinancials(symbol) {
  if (!supabase) return EMPTY;
  const { data: company, error: cErr } = await supabase
    .from("companies").select("cik, ticker, name").eq("ticker", symbol).maybeSingle();
  if (cErr || !company?.cik) return EMPTY;

  const { data: rows, error } = await supabase
    .from("company_financials")
    .select("concept, xbrl_tag, fiscal_year, fiscal_period, period_start, period_end, value, unit, form, accession_number, filed_date, source_url")
    .eq("cik", company.cik)
    .eq("fiscal_period", "FY")
    .order("period_end", { ascending: true });
  if (error) {
    console.warn("[companyFinancials] read failed:", error.code, error.message);
    return { ...EMPTY, company };
  }

  const grouped = {};
  let latestFiling = null;
  for (const r of rows || []) {
    const row = { ...r, value: Number(r.value), fiscal_year: fiscalYearOf(r.period_end, r.fiscal_year) };
    (grouped[r.concept] ||= []).push(row);
    if (!latestFiling || r.filed_date > latestFiling.filed_date) latestFiling = row;
  }
  const { byConcept, stale, otherDefinition } = shapeSeries(grouped);

  // Free cash flow — the one derivation. Only for a period where BOTH parts
  // were reported; the row says which two rows it came from.
  const derived = {};
  const cfo = byConcept.cfo || [], capex = byConcept.capex || [];
  const fcf = [];
  for (const c of cfo) {
    const k = capex.find(x => x.period_end === c.period_end);
    if (!k) continue;
    fcf.push({
      concept: "fcf", fiscal_year: c.fiscal_year, period_end: c.period_end, unit: c.unit,
      value: c.value - k.value,
      from: { cfo: c, capex: k },
      // the later of the two filings is the one that fixed this number
      filed_date: c.filed_date > k.filed_date ? c.filed_date : k.filed_date,
      form: c.form, source_url: c.source_url,
    });
  }
  if (fcf.length) derived.fcf = fcf;

  return { company, byConcept, derived, latestFiling, stale, otherDefinition };
}

// ─── One definition per series, and nothing stale in the headline ───────────
// Sprint 13, 6.4 (checked on LEN, fiscal year ending November):
//
//   • Balance items can arrive under tags that MEAN different things: cash
//     with or without restricted cash, debt with or without its current part.
//     The server merges tags per period (a rename, like Apple's revenue in
//     2018, is the same line), so a series could switch definition mid-way ·
//     LEN's cash was "with restricted" for 2023 and "without" after. For these
//     concepts a series keeps only the rows under its latest tag; the years
//     under another tag are counted, not drawn beside them.
//   • A concept whose last reported year is two or more years behind the
//     company's latest fiscal year is not a current figure. LEN stopped
//     tagging operating income after FY2020, and the row led with "$3.48B,
//     FY2020" among its FY2025 numbers. It moves out of the figures, stated
//     as "last reported for FY…".
const ONE_DEFINITION = new Set(["cash", "long_term_debt", "debt_current"]);

// A 52/53-week year that ends on the Saturday nearest 31 December can end on
// 1–7 January: that year is the one before (the company names it so). Without
// this, two periods fell in one year and the earlier one vanished.
export function fiscalYearOf(periodEnd, stored) {
  const m = /^(\d{4})-01-0([1-7])/.exec(String(periodEnd || ""));
  return m ? Number(m[1]) - 1 : stored;
}

// The company's latest fiscal year: the one most concepts end on (the latest
// of those when tied), not the single latest row · one stray later-dated row
// must not make every other concept look stale.
function latestYearOf(grouped) {
  const ends = Object.values(grouped).filter(r => r.length).map(r => r[r.length - 1].fiscal_year || 0);
  if (!ends.length) return 0;
  const count = new Map();
  for (const y of ends) count.set(y, (count.get(y) || 0) + 1);
  return [...count.entries()].sort((a, b) => (b[1] - a[1]) || (b[0] - a[0]))[0][0];
}

export function shapeSeries(grouped) {
  const latestFY = latestYearOf(grouped);
  const byConcept = {}, stale = {}, otherDefinition = {};
  for (const [concept, rows] of Object.entries(grouped)) {
    let series = rows;
    if (ONE_DEFINITION.has(concept) && rows.length) {
      const tag = rows[rows.length - 1].xbrl_tag;
      series = rows.filter(r => r.xbrl_tag === tag);
      const other = rows.filter(r => r.xbrl_tag !== tag).map(r => r.fiscal_year);
      if (other.length) otherDefinition[concept] = other;
    }
    const last = series.length ? series[series.length - 1].fiscal_year : null;
    if (last != null && latestFY - last >= 2) { stale[concept] = last; continue; }
    byConcept[concept] = series;
  }
  return { byConcept, stale, otherDefinition };
}

// null while loading; never throws.
export function useCompanyFinancials(symbol) {
  const [state, setState] = useState(null);
  useEffect(() => {
    let alive = true;
    if (!symbol) { setState(EMPTY); return; }
    setState(null);
    loadFinancials(String(symbol).toUpperCase())
      .then(r => { if (alive) setState(r); })
      .catch(() => { if (alive) setState(EMPTY); });
    return () => { alive = false; };
  }, [symbol]);
  return state;
}

// The last N fiscal years of a series, oldest first, as consecutive years:
// a year the series does not have is a gap ({ fiscal_year, missing: true }),
// never the next reported year drawn beside it (sprint 13, 6.4 · LEN's debt
// went 2020 → 2024 and read as consecutive).
// The years reported under another definition that fall inside the span a
// chart shows (sprint 14, 3.6). "2012, 2013" under a chart of 2021–2025
// explained a gap that was not on the screen.
export function yearsInSpan(years, shown) {
  if (!years || !years.length || !shown || !shown.length) return [];
  const first = Number(shown[0].fiscal_year), last = Number(shown[shown.length - 1].fiscal_year);
  return years.filter(y => Number(y) >= first && Number(y) <= last);
}

export function lastYears(rows, n = 5) {
  const list = rows || [];
  if (!list.length) return [];
  const byYear = new Map(list.map(r => [r.fiscal_year, r]));
  const latest = list[list.length - 1].fiscal_year;
  const out = [];
  for (let y = latest - n + 1; y <= latest; y++) {
    if (byYear.has(y)) out.push(byYear.get(y));
    else if (y >= list[0].fiscal_year) out.push({ fiscal_year: y, missing: true, period_end: `gap-${y}` });
  }
  return out;
}
